import React, { useCallback, useEffect, useMemo } from 'react';
import { type NavigateOptions, type To, useLocation, useNavigate, useParams } from 'react-router-dom';
import ScreenContainer from '../components/ScreenContainer';
import SplitTransactionView from '../components/SplitTransactionView';
import { useNavigation } from '../hooks/useNavigation';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';
import type { Transaction } from '../types';

interface SplitTransactionRouteState {
  transaction?: Transaction;
}

interface SplitTransactionScreenProps {
  getTransactionDetailPath?: (transaction: Transaction) => To;
  getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
  fallbackDetailPath?: (transactionId: number) => To;
  fallbackListPath?: To;
}

const findTransactionById = (transactions: Transaction[], targetId: number): Transaction | null => {
  for (const transaction of transactions) {
    if (transaction.id === targetId) {
      return transaction;
    }

    const subTransaction = transaction.subTransactions?.find((sub) => sub.id === targetId);
    if (subTransaction) {
      return subTransaction;
    }
  }

  return null;
};

const SplitTransactionScreen: React.FC<SplitTransactionScreenProps> = ({
  getTransactionDetailPath,
  getTransactionNavigationState,
  fallbackDetailPath,
  fallbackListPath = '/transactions',
}) => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { goBack } = useNavigation();
  const dispatch = useAppDispatch();

  const parsedTransactionId = Number(transactionId);
  const isTransactionIdValid = Number.isFinite(parsedTransactionId);
  const routeState = location.state as SplitTransactionRouteState | null;
  const routeTransaction =
    routeState?.transaction && routeState.transaction.id === parsedTransactionId
      ? routeState.transaction
      : null;

  const { transactions } = useAppSelector((state) => state.transactions);
  const { status: tagsStatus } = useAppSelector((state) => state.tags);
  const tagMap = useAppSelector(selectTagMap);

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsStatus]);

  const resolvedTransaction = useMemo(() => {
    if (!isTransactionIdValid) {
      return null;
    }

    return findTransactionById(transactions, parsedTransactionId) ?? routeTransaction;
  }, [isTransactionIdValid, parsedTransactionId, routeTransaction, transactions]);

  const closeSplitScreen = useCallback(() => {
    if (window.history.length > 1) {
      goBack();
      return;
    }

    if (isTransactionIdValid) {
      navigate(fallbackDetailPath?.(parsedTransactionId) ?? `/transactions/${parsedTransactionId}`, { replace: true });
      return;
    }

    navigate(fallbackListPath, { replace: true });
  }, [fallbackDetailPath, fallbackListPath, goBack, isTransactionIdValid, navigate, parsedTransactionId]);

  if (!isTransactionIdValid) {
    return (
      <ScreenContainer title="Split Details">
        <div className="mx-auto w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Invalid transaction selected.
        </div>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer title="Split Details">
      {resolvedTransaction ? (
        <div className="mx-auto w-full max-w-sm h-full">
          <SplitTransactionView
            transaction={resolvedTransaction}
            tagMap={tagMap}
            onClose={closeSplitScreen}
            getTransactionDetailPath={getTransactionDetailPath}
            getTransactionNavigationState={getTransactionNavigationState}
          />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-sm px-6 pt-12 text-center text-muted-foreground">
          Transaction not found.
        </div>
      )}
    </ScreenContainer>
  );
};

export default SplitTransactionScreen;
